/**
 * Lodestone Map utilities
 * Provides reusable map initialization and rendering
 */

import type { Map as MapboxMap, Marker, LngLatBoundsLike, MarkerOptions as MapboxMarkerOptions, ExpressionSpecification } from 'mapbox-gl';
import type { LngLat, Waypoint, WaypointTypeConfig, WaypointTypesMap, MapOptions, RouteOptions, MarkerOptions } from './types';
import { COLORS } from './colors';
import { createWaypointMarker } from './route-detail';

// Waypoint type styling configuration
export const WAYPOINT_TYPES: WaypointTypesMap = {
    'camp': { color: '#16a34a', icon: 'ph-tent', label: 'Campsite' },
    'water': { color: '#3b82f6', icon: 'ph-drop', label: 'Water Source' },
    'viewpoint': { color: '#a855f7', icon: 'ph-binoculars', label: 'Viewpoint' },
    'trailhead': { color: '#f97316', icon: 'ph-signpost', label: 'Trailhead' },
    'peak': { color: '#f59e0b', icon: 'ph-mountains', label: 'Peak' },
    'hazard': { color: '#ef4444', icon: 'ph-warning', label: 'Hazard' },
    'poi': { color: '#06b6d4', icon: 'ph-map-pin', label: 'Point of Interest' }
};

const DEFAULT_WAYPOINT_CONFIG: WaypointTypeConfig = { color: '#6b7280', icon: 'ph-map-pin', label: 'Waypoint' };

/**
 * Get the styling config for a waypoint type, with a default fallback.
 */
export function getWaypointTypeConfig(type?: string): WaypointTypeConfig {
    return WAYPOINT_TYPES[type || ''] || DEFAULT_WAYPOINT_CONFIG;
}

/**
 * Create a hover marker DOM element (circle with white border and shadow).
 * Used by MapManager, ElevationChartMapSync, trip-map, and elevation-auto.
 */
export function createHoverMarkerElement(color: string, size = 12): HTMLDivElement {
    const el = document.createElement('div');
    el.className = 'hover-marker';
    el.style.cssText = `width:${size}px;height:${size}px;background:${color};border:2px solid white;border-radius:50%;box-shadow:0 2px 4px rgba(0,0,0,0.3);pointer-events:none;`;
    return el;
}

/**
 * Create a lettered stop marker element (A, B, C…) for planner stops on a map.
 * The detail page and the planner share it so stops look the same everywhere.
 */
export function createStopMarkerElement(label: string, size = 26, variant: 'start' | 'middle' | 'end' = 'middle'): HTMLDivElement {
    const el = document.createElement('div');
    el.className = 'stop-marker';
    el.setAttribute('role', 'img');
    el.setAttribute('aria-label', `Stop ${label}`);
    el.dataset.variant = variant;
    const look = variant === 'start'
        ? 'background:#19362D;color:#fff;border-color:#fff'
        : variant === 'end'
            ? 'background:#66CC8A;color:#0B1F17;border-color:#fff'
            : 'background:#fff;color:#19362D;border-color:#19362D';
    el.style.cssText = `width:${size}px;height:${size}px;box-sizing:border-box;border-radius:50%;border:3px solid #fff;${look};box-shadow:0 2px 6px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;font:700 ${Math.round(size * 0.46)}px/1 'Space Grotesk',system-ui,sans-serif;letter-spacing:0.02em;pointer-events:none;`;
    el.textContent = label;
    return el;
}

/**
 * Letter for the nth stop: A..Z, then AA, AB, …
 */
export function stopLabel(index: number): string {
    let n = index;
    let label = '';
    do {
        label = String.fromCharCode(65 + (n % 26)) + label;
        n = Math.floor(n / 26) - 1;
    } while (n >= 0);
    return label;
}

/**
 * Set the Mapbox access token on the global mapboxgl object.
 * Centralizes the `as unknown` cast to one place.
 */
export function setMapboxToken(token: string): void {
    (mapboxgl as unknown as { accessToken: string }).accessToken = token;
}

// Cached token from API
let cachedMapboxToken: string | null = null;

/**
 * Fetch the Mapbox token from the API (cached after first call).
 * Usable from both TS modules and inline templ scripts via Lodestone.fetchMapboxToken().
 */
export async function fetchMapboxToken(): Promise<string> {
    if (cachedMapboxToken) return cachedMapboxToken;
    const response = await fetch('/api/config/mapbox-token', { credentials: 'include' });
    const data = await response.json() as { token: string };
    cachedMapboxToken = data.token;
    return cachedMapboxToken;
}

/**
 * Add 3D terrain (raster-dem) and atmospheric sky layer to a Mapbox map.
 * Used by the flyover (shared/flyover/FlyoverMap.ts).
 */
export function addTerrainAndSky(map: MapboxMap, exaggeration = 1.2): void {
    map.addSource('mapbox-dem', {
        type: 'raster-dem',
        url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
        tileSize: 512,
        maxzoom: 14
    });
    map.setTerrain({ source: 'mapbox-dem', exaggeration });
    map.addLayer({
        id: 'sky',
        type: 'sky',
        paint: {
            'sky-type': 'atmosphere',
            'sky-atmosphere-sun': [0.0, 90.0],
            'sky-atmosphere-sun-intensity': 15
        }
    });
}

// Map styles for light and dark themes
const MAP_STYLES: Record<string, string> = {
    light: 'mapbox://styles/mapbox/outdoors-v12',
    dark: 'mapbox://styles/mapbox/dark-v11'
};

// Dark theme names used for popup/UI styling (not map tiles)
const DARK_THEMES = ['dim', 'night', 'dark', 'dracula', 'coffee', 'forest', 'lodestone-dark'];

/**
 * Check if current app theme is dark (for popup/UI colors, not map tiles)
 */
export function isDarkTheme(): boolean {
    const theme = document.documentElement.getAttribute('data-theme');
    return theme ? DARK_THEMES.includes(theme) : false;
}

/**
 * Get the appropriate map style based on user preference.
 * Defaults to light (outdoors) regardless of app theme.
 * Users can opt into dark maps via the "Dark Maps" toggle in Settings.
 */
export function getThemeMapStyle(): string {
    return localStorage.getItem('darkMaps') === 'true' ? MAP_STYLES.dark : MAP_STYLES.light;
}

/**
 * Draw trails on the outdoors-v12 basemap as a crisp dotted pattern —
 * AllTrails-style legibility without turning the trail casings chunky.
 *
 * Scopes the dots to real trails only: `road-path` features with
 * `type in (path, track, cycleway)`, while hiding urban variants
 * (`sidewalk`, `crossing`, `service`) via zero opacity. Also hides
 * `road-pedestrian` and `road-steps` — those are urban plazas/stairs, not
 * hiking.
 *
 * Never touches `*-case`/`*-bg` layers — restyling those is what made trails
 * look like solid black blobs in an earlier attempt. Skipped on dark basemap.
 */
export function applyTrailEmphasis(map: MapboxMap): void {
    if (localStorage.getItem('darkMaps') === 'true') return;

    // Tiny dash + 2x gap, scaled by line-width → crisp dot pattern.
    const dotted: [number, number] = [0.5, 2];
    const trailColor = '#4a4a4a';

    // Type-gated opacity: 0 for urban types, 0.95 for genuine trails.
    const trailOnlyOpacity = [
        'match',
        ['get', 'type'],
        ['sidewalk', 'crossing', 'service'], 0,
        0.95
    ] as unknown as ExpressionSpecification;

    if (map.getLayer('road-path')) {
        try { map.setPaintProperty('road-path', 'line-color', trailColor); } catch { /* not a line layer */ }
        try { map.setPaintProperty('road-path', 'line-opacity', trailOnlyOpacity); } catch { /* not a line layer */ }
        try { map.setPaintProperty('road-path', 'line-dasharray', dotted); } catch { /* not a line layer */ }
    }

    // Pedestrian plazas and urban stairs aren't trails — hide.
    ['road-pedestrian', 'road-steps'].forEach((id) => {
        if (!map.getLayer(id)) return;
        try { map.setPaintProperty(id, 'line-opacity', 0); } catch { /* not a line layer */ }
    });
}

/**
 * MapManager class for creating and managing Mapbox GL maps
 */
export class MapManager {
    #map: MapboxMap | null = null;
    #containerId: string;
    #options: MapOptions;
    #token: string | null = null;
    #hoverMarker: Marker | null = null;
    #markers: Marker[] = [];
    #coordinates: LngLat[] = [];

    constructor(containerId: string, options: MapOptions = {}) {
        this.#containerId = containerId;
        this.#options = {
            center: [-98.5795, 39.8283] as LngLat, // US center
            zoom: 12,
            style: options.style || getThemeMapStyle(),
            controls: true,
            fullscreen: false,
            ...options
        };
    }

    /**
     * Get the underlying Mapbox map instance
     */
    get map(): MapboxMap | null {
        return this.#map;
    }

    /**
     * Get the stored coordinates
     */
    get coordinates(): LngLat[] {
        return this.#coordinates;
    }

    /**
     * Get the hover marker
     */
    get hoverMarker(): Marker | null {
        return this.#hoverMarker;
    }

    /**
     * Fetch Mapbox token from API
     */
    async fetchToken(): Promise<string> {
        if (this.#token) return this.#token;
        this.#token = await fetchMapboxToken();
        return this.#token;
    }

    /**
     * Initialize the map
     */
    async init(): Promise<MapManager> {
        // Ensure mapboxgl is loaded
        if (typeof mapboxgl === 'undefined') {
            throw new Error('Mapbox GL JS not loaded');
        }

        // Fetch and set token
        await this.fetchToken();
        setMapboxToken(this.#token!);

        // Create the map
        this.#map = new mapboxgl.Map({
            container: this.#containerId,
            style: this.#options.style,
            center: this.#options.center,
            zoom: this.#options.zoom
        });

        // Add controls
        if (this.#options.controls) {
            this.#map.addControl(new mapboxgl.NavigationControl(), 'top-right');
        }
        if (this.#options.fullscreen) {
            this.#map.addControl(new mapboxgl.FullscreenControl());
        }

        // Return promise that resolves when map is loaded
        return new Promise((resolve) => {
            this.#map!.on('load', () => {
                applyTrailEmphasis(this.#map!);
                resolve(this);
            });
        });
    }

    /**
     * Set the map style
     */
    setStyle(styleUrl: string): MapManager {
        if (!this.#map) return this;

        this.#map.setStyle(styleUrl);

        this.#map.once('style.load', () => {
            applyTrailEmphasis(this.#map!);
            // Re-add route after style loads if we have coordinates
            if (this.#coordinates.length > 0) {
                this.addRoute(this.#coordinates);
            }
        });

        return this;
    }

    /**
     * Add a route line to the map
     */
    addRoute(coordinates: LngLat[], options: RouteOptions = {}): MapManager {
        const {
            color = COLORS.routeLine,
            width = 5,
            sourceId = 'route',
            layerId = 'route-line'
        } = options;

        this.#coordinates = coordinates;

        // Add GeoJSON source
        this.#map!.addSource(sourceId, {
            type: 'geojson',
            data: {
                type: 'Feature',
                properties: {},
                geometry: {
                    type: 'LineString',
                    coordinates: coordinates
                }
            }
        });

        // White casing underneath (AllTrails-style halo).
        this.#map!.addLayer({
            id: `${layerId}-casing`,
            type: 'line',
            source: sourceId,
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: {
                'line-color': '#ffffff',
                'line-width': width + 3,
                'line-opacity': 0.85
            }
        });

        // Main route line
        this.#map!.addLayer({
            id: layerId,
            type: 'line',
            source: sourceId,
            layout: {
                'line-join': 'round',
                'line-cap': 'round'
            },
            paint: {
                'line-color': color,
                'line-width': width,
                'line-opacity': 0.95
            }
        });

        return this;
    }

    /**
     * Fit map bounds to show the entire route
     */
    fitToRoute(padding = 50): MapManager {
        if (this.#coordinates.length === 0) return this;

        const bounds = this.#coordinates.reduce((bounds, coord) => {
            return bounds.extend(coord as [number, number]);
        }, new mapboxgl.LngLatBounds(this.#coordinates[0], this.#coordinates[0]));

        this.#map!.fitBounds(bounds as LngLatBoundsLike, { padding });
        return this;
    }

    /**
     * Add start and end markers for a route
     */
    addRouteMarkers(options: MarkerOptions = {}): MapManager {
        if (this.#coordinates.length === 0) return this;

        const { startColor = COLORS.startMarker, endColor = COLORS.endMarker } = options;

        // Start marker
        const startMarker = new mapboxgl.Marker({ color: startColor })
            .setLngLat(this.#coordinates[0])
            .addTo(this.#map!);
        this.#markers.push(startMarker);

        // End marker (if different from start)
        if (this.#coordinates.length > 1) {
            const endMarker = new mapboxgl.Marker({ color: endColor })
                .setLngLat(this.#coordinates[this.#coordinates.length - 1])
                .addTo(this.#map!);
            this.#markers.push(endMarker);
        }

        return this;
    }

    /**
     * Add a hover marker for chart interaction
     */
    addHoverMarker(options: { color?: string } = {}): MapManager {
        const { color = COLORS.hoverMarker } = options;

        const el = createHoverMarkerElement(color);

        this.#hoverMarker = new mapboxgl.Marker({ element: el })
            .setLngLat(this.#coordinates[0] || [0, 0])
            .addTo(this.#map!);

        // Hide initially
        this.#hoverMarker.getElement().style.display = 'none';

        return this;
    }

    /**
     * Show the hover marker at a specific position
     */
    showHoverMarker(lngLat: LngLat): void {
        if (!this.#hoverMarker) return;
        this.#hoverMarker.setLngLat(lngLat);
        this.#hoverMarker.getElement().style.display = 'block';
    }

    /**
     * Hide the hover marker
     */
    hideHoverMarker(): void {
        if (!this.#hoverMarker) return;
        this.#hoverMarker.getElement().style.display = 'none';
    }

    /**
     * Add waypoint markers to the map
     */
    addWaypoints(waypoints: Waypoint[]): MapManager {
        if (!waypoints || waypoints.length === 0 || !this.#map) return this;

        waypoints.forEach((wp, idx) => {
            const marker = createWaypointMarker(wp, idx, mapboxgl, this.#map!);
            this.#markers.push(marker);
        });

        return this;
    }

    /**
     * Add a simple marker to the map
     */
    addMarker(lngLat: LngLat, options: MapboxMarkerOptions = {}): Marker {
        const marker = new mapboxgl.Marker(options)
            .setLngLat(lngLat)
            .addTo(this.#map!);
        this.#markers.push(marker);
        return marker;
    }

    /**
     * Remove all markers from the map
     */
    clearMarkers(): void {
        this.#markers.forEach(marker => marker.remove());
        this.#markers = [];
    }

    /**
     * Set the map center
     */
    setCenter(center: LngLat, options: { animate?: boolean; zoom?: number; duration?: number } = {}): void {
        if (options.animate !== false) {
            this.#map!.flyTo({ center, ...options });
        } else {
            this.#map!.setCenter(center);
        }
    }

    /**
     * Enable 3D terrain rendering
     */
    enable3DTerrain(exaggeration: number = 1.5): MapManager {
        if (!this.#map) return this;

        // Add DEM source if not present
        if (!this.#map.getSource('mapbox-dem')) {
            this.#map.addSource('mapbox-dem', {
                type: 'raster-dem',
                url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
                tileSize: 512,
                maxzoom: 14
            });
        }

        // Enable terrain
        this.#map.setTerrain({ source: 'mapbox-dem', exaggeration });

        // Add sky layer for atmospheric effect
        if (!this.#map.getLayer('sky')) {
            this.#map.addLayer({
                id: 'sky',
                type: 'sky',
                paint: {
                    'sky-type': 'atmosphere',
                    'sky-atmosphere-sun': [0.0, 90.0],
                    'sky-atmosphere-sun-intensity': 15
                }
            });
        }

        return this;
    }

    /**
     * Disable 3D terrain rendering
     */
    disable3DTerrain(): MapManager {
        if (!this.#map) return this;

        this.#map.setTerrain(null);

        if (this.#map.getLayer('sky')) {
            this.#map.removeLayer('sky');
        }

        return this;
    }

    /**
     * Check if 3D terrain is currently enabled
     */
    is3DTerrainEnabled(): boolean {
        if (!this.#map) return false;
        return this.#map.getTerrain() !== null;
    }

    /**
     * Destroy the map and clean up all resources
     */
    destroy(): void {
        this.clearMarkers();

        if (this.#hoverMarker) {
            this.#hoverMarker.remove();
            this.#hoverMarker = null;
        }

        if (this.#map) {
            this.#map.remove();
            this.#map = null;
        }

        this.#coordinates = [];
    }

}

